import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import axiosRetry from 'axios-retry';

import instance from 'configs/requests';

// Don't save page/limit... filters. We want to save it in the local state so the patients table
// can keep track of it during the page session but it should be reset on reloads
const localStateOnlyPatientsFilters = ['page', 'limit', 'search'];

export const getCitc = createAsyncThunk('settings/getCitc', async () => {
  axiosRetry(instance, { retries: 3 });
  const { data } = await instance.get('/clinician-gateway/settings/citc');
  return data;
});

export const fetchAccountAttributes = createAsyncThunk(
  '/settings/getAccountAttributes',
  async (aid: string) => {
    const { data } = await instance.get(`/clinician-gateway/accounts/${aid}/attributes`);
    return data as IAccountAttribute[];
  }
);

export const getPatientsCustomViews = createAsyncThunk(
  'settings/getPatientsCustomViews',
  async () => {
    const response = await instance.get('/clinician-gateway/patient-views');

    const responseParsed = response.data.map((customView: any) => {
      return mapPatientsCustomViewResponseToInstance(customView);
    }) as ICustomViewsPatients[];
    return responseParsed;
  }
);

export const getAllDeviceOrderings = createAsyncThunk('getDeviceOrderings', async () => {
  const { data } = await instance.get('/clinician-gateway/device-orderings');
  return data as IDeviceOrderingCatalogItem[];
});

export const createPatientsCustomViews = createAsyncThunk(
  'settings/createPatientsCustomViews',
  async (
    params: {
      aid: string;
      view: ICustomViewsPatients;
      viewName: string;
      gid: string | null;
    },
    thunkAPI
  ) => {
    const payload: any = {
      name: params.viewName,
      aid: params.aid,
      organizationGid: params.gid,
      columnsConfiguration: JSON.stringify(params.view.columnsConfiguration),
      filtersParams: JSON.stringify(
        params.view.filtersParams.filter(
          (f) => !localStateOnlyPatientsFilters.includes(f.filterLabel)
        )
      )
    };

    const response = await instance.post('/clinician-gateway/patient-views', payload);

    const newView = response.data;
    newView.filtersParams = JSON.parse(newView.filtersParams);
    newView.columnsConfiguration = JSON.parse(newView.columnsConfiguration);
    newView.isDefault = +newView.isDefault;

    // Need to update the views order after creating a view since it's automatically opened
    const { settings } = thunkAPI.getState() as { settings: ISettingsState };
    if (settings.customViews.patients.viewsOrder) {
      const updatedViewsOrder = _.cloneDeep(settings.customViews.patients.viewsOrder);
      const viewsOrderGid = params.gid === null ? 'all' : params.gid;
      if (updatedViewsOrder.sorted[viewsOrderGid]) {
        updatedViewsOrder.sorted[viewsOrderGid].push(newView.id);
      } else {
        updatedViewsOrder.sorted[viewsOrderGid] = [newView.id];
      }

      thunkAPI.dispatch(
        patchPatientsCustomViewsOrder({
          viewsOrder: updatedViewsOrder
        })
      );
    }

    return newView as ICustomViewsPatients;
  }
);

export const patchPatientsCustomViews = createAsyncThunk(
  'settings/patchPatientsCustomViews',
  async (params: { updatedView: ICustomViewsPatients }) => {
    const payload: any = { id: params.updatedView.id };
    if (params.updatedView.columnsConfiguration) {
      payload.columnsConfiguration = JSON.stringify(params.updatedView.columnsConfiguration);
    }
    if (params.updatedView.filtersParams) {
      payload.filtersParams = JSON.stringify(
        params.updatedView.filtersParams.filter(
          (f) => !localStateOnlyPatientsFilters.includes(f.filterLabel)
        )
      );
    }

    const response = await instance.patch('/clinician-gateway/patient-views', payload);
    const updatedView = mapPatientsCustomViewResponseToInstance(response.data);
    return updatedView as ICustomViewsPatients;
  }
);

export const deletePatientsCustomView = createAsyncThunk(
  'settings/deletePatientsCustomViews',
  async (params: { aid: string; gid: string; viewId: number }, thunkAPI) => {
    await instance.delete(`/clinician-gateway/patient-views/${params.viewId}`);

    // Update views order
    const { settings } = thunkAPI.getState() as { settings: ISettingsState };
    if (settings.customViews.patients.viewsOrder) {
      const updatedViewsOrder = _.cloneDeep(settings.customViews.patients.viewsOrder);
      updatedViewsOrder.sorted[params.gid] = updatedViewsOrder.sorted[params.gid].filter(
        (id) => id !== params.viewId
      );

      thunkAPI.dispatch(
        patchPatientsCustomViewsOrder({
          viewsOrder: updatedViewsOrder
        })
      );
    }

    return params.viewId;
  }
);

export const getPatientsCustomViewsOrder = createAsyncThunk(
  'settings/getPatientsCustomViewsOrder',
  async () => {
    const response = await instance.get('/clinician-gateway/patient-views/order');
    return mapPatientsCustomViewsOrderResponseToInstance(response.data);
  }
);

export const patchPatientsCustomViewsOrder = createAsyncThunk(
  'settings/patchPatientsCustomViewsOrder',
  async (params: { viewsOrder: ICustomViewsOrder }) => {
    const payload = {
      id: params.viewsOrder.id,
      sorted: JSON.stringify(params.viewsOrder.sorted)
    };

    const response = await instance.patch('/clinician-gateway/patient-views/order', payload);
    return mapPatientsCustomViewsOrderResponseToInstance(response.data);
  }
);

const mapPatientsCustomViewResponseToInstance = (payload: any) => {
  if (!payload) {
    throw new Error('Invalid server response');
  }

  return {
    ...payload,
    columnsConfiguration: JSON.parse(payload.columnsConfiguration),
    filtersParams: JSON.parse(payload.filtersParams)
  } as ICustomViewsPatients;
};

const mapPatientsCustomViewsOrderResponseToInstance = (payload: any) => {
  if (!payload) {
    throw new Error('Invalid server response');
  }

  return {
    ...payload,
    sorted: JSON.parse(payload.sorted)
  } as ICustomViewsOrder;
};
